import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import BrandIntro from "@/components/BrandIntro";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Outcomes from "@/pages/Outcomes";
import Sectors from "@/pages/Sectors";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import Brand from "@/pages/Brand";
import Book from "@/pages/Book";
import Growth from "@/pages/Growth";
import Adviser from "@/pages/Adviser";
import OpportunityBuilder from "@/pages/OpportunityBuilder";
import AdminLogin from "@/pages/AdminLogin";
import { Overview, Leads, Notifications } from "@/pages/AdminDashboard";
import AdminLead from "@/pages/AdminLead";
import AdminClients from "@/pages/AdminClients";
import AdminClient from "@/pages/AdminClient";
import ClientPortal from "@/pages/ClientPortal";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        const t = setTimeout(() => el.scrollIntoView({ behavior: "instant", block: "start" }), 80);
        return () => clearTimeout(t);
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    return undefined;
  }, [pathname, hash]);
  return null;
}

export default function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-navy text-slate-100 font-sans">
        <BrandIntro />
        <Nav />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/outcomes" element={<Outcomes />} />
            <Route path="/how-we-work" element={<Navigate to="/outcomes" replace />} />
            <Route path="/sectors" element={<Sectors />} />
            <Route path="/about" element={<About />} />
            <Route path="/founder" element={<Navigate to="/about#founder" replace />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/brand" element={<Brand />} />
            <Route path="/book" element={<Book />} />
            <Route path="/growth" element={<Growth />} />
            <Route path="/growth/adviser" element={<Adviser />} />
            <Route path="/growth/builder" element={<OpportunityBuilder />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Overview />} />
            <Route path="/admin/leads" element={<Leads />} />
            <Route path="/admin/leads/:id" element={<AdminLead />} />
            <Route path="/admin/notifications" element={<Notifications />} />
            <Route path="/admin/clients" element={<AdminClients />} />
            <Route path="/admin/clients/:id" element={<AdminClient />} />
            <Route path="/clients/:slug" element={<ClientPortal />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
        <Footer />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </BrowserRouter>
  );
}
